/**
 * Performance URL state: the Focus scope with every control on (All apps,
 * Status, thresholds and the 24-hour activity filter), plus one key of its
 * own: `asset=<asset id>`, a deep link that opens that creative's inspector
 * on arrival (e.g. from an Overview winner or finding); closing the drawer
 * removes it. Table settings live in contract.ts, not the URL.
 * Keys, validation and API encoding: `@/features/focus` (focus/search.ts).
 */
import { defineFocusConfig, parseFocusSearch, type FocusControls, type FocusSearch } from '@/features/focus';
import { defineSearch, param, type RawSearch } from '@/router/search';

const CONTROLS = {
  allApps: true,
  dates: true,
  filters: { status: true, thresholds: true, activity: true },
} as const satisfies FocusControls;

/** The typed URL. Field names ARE the URL keys (the router writes them as is). */
export interface PerformanceSearch extends FocusSearch {
  /** The creative (asset id) whose inspector opens on arrival. */
  asset?: string | undefined;
}

export const parsePerformanceSearch = (raw: RawSearch): PerformanceSearch => ({ ...parseFocusSearch(raw, CONTROLS), asset: param.string(raw, 'asset') });

export const performanceSearch = defineSearch(parsePerformanceSearch);

export const PERFORMANCE_FOCUS = defineFocusConfig({
  ...CONTROLS,
  parse: parsePerformanceSearch,
  // A new scope is a new question: a deep-linked creative does not follow it.
  onScopeChange: (next) => ({ ...next, asset: undefined }),
  appSwitcher: {
    evidenceFor: 'statuses and insights',
    hint: 'Click an app to look at it alone, or tick several to combine them.',
  },
});
